import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { JudgeFormFieldset } from '@/components/molecules/JudgeFormFieldset';
import type { ChecklistCriterion, ChecklistJudge, JudgeKind, RubricJudge, RubricScale } from '@/schemas/judges';
import type { JudgeDraft } from '@/services/Judges';

import { BUTTON_DANGER, BUTTON_GHOST, BUTTON_PRIMARY, INPUT_BASE, TEXTAREA_BASE } from './JudgeForm.constants';
import type { JudgeFormProps } from './JudgeForm.types';

type FormState = {
	name: string;
	description: string;
	enabled: boolean;
	modulesCsv: string;
	agentsCsv: string;
	prompt: string;
	scale: RubricScale;
	contextEnabled: boolean;
	contextNamespace: string;
	contextTopK: string;
	criteria: ChecklistCriterion[];
};

const csvToArr = (v: string): string[] =>
	v
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);

const arrToCsv = (a: string[]): string => a.join(', ');

const initialCriteria = (kind: JudgeKind, initial?: JudgeFormProps['initialValue']): ChecklistCriterion[] => {
	if (initial?.kind === 'checklist') return initial.criteria;
	if (kind === 'checklist') return [{ id: `crit-${Date.now()}`, label: '', description: '' }];
	return [];
};

const initialFormState = (kind: JudgeKind, initial?: JudgeFormProps['initialValue']): FormState => {
	const qdrantSource =
		initial?.kind === 'rubric' ? initial.contextSources.find((s) => s.kind === 'qdrant_chunks') : null;

	return {
		name: initial?.name ?? '',
		description: initial?.description ?? '',
		enabled: initial?.enabled ?? true,
		modulesCsv: arrToCsv(initial?.appliesTo.modules ?? []),
		agentsCsv: arrToCsv(initial?.appliesTo.agents ?? []),
		prompt: initial && initial.kind === 'rubric' ? initial.prompt : '',
		scale: initial && initial.kind === 'rubric' ? initial.scale : 'pass_fail',
		contextEnabled: Boolean(qdrantSource),
		contextNamespace: qdrantSource?.namespace ?? 'support',
		contextTopK: String(qdrantSource?.topK ?? 5),
		criteria: initialCriteria(kind, initial),
	};
};

const normalizeTopK = (raw: string): number => {
	const n = Number.parseInt(raw, 10);

	if (!Number.isFinite(n)) return 5;
	return Math.min(20, Math.max(1, n));
};

const buildDraft = (kind: JudgeKind, state: FormState): JudgeDraft => {
	const base = {
		name: state.name.trim(),
		description: state.description.trim(),
		enabled: state.enabled,
		appliesTo: { modules: csvToArr(state.modulesCsv), agents: csvToArr(state.agentsCsv) },
	};

	if (kind === 'rubric') {
		return {
			...base,
			kind: 'rubric',
			prompt: state.prompt.trim(),
			scale: state.scale,
			contextSources: state.contextEnabled
				? [
						{
							kind: 'qdrant_chunks',
							namespace: state.contextNamespace.trim() || 'support',
							topK: normalizeTopK(state.contextTopK),
						},
					]
				: [],
		} satisfies Omit<RubricJudge, 'id' | 'createdAt' | 'updatedAt'>;
	}
	return {
		...base,
		kind: 'checklist',
		criteria: state.criteria.filter((c) => c.label.trim().length > 0),
	} satisfies Omit<ChecklistJudge, 'id' | 'createdAt' | 'updatedAt'>;
};

export const JudgeForm = ({
	mode,
	initialKind,
	initialValue,
	onSubmit,
	onCancel,
	onDelete,
	isSubmitting,
	isDeleting,
}: JudgeFormProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'judgeDetail' });
	const [state, setState] = useState<FormState>(initialFormState(initialKind, initialValue));
	const kind = initialValue?.kind ?? initialKind;

	const handleAddCriterion = () =>
		setState((s) => ({ ...s, criteria: [...s.criteria, { id: `crit-${Date.now()}`, label: '', description: '' }] }));
	const handleRemoveCriterion = (id: string) =>
		setState((s) => ({ ...s, criteria: s.criteria.filter((c) => c.id !== id) }));
	const handleCriterionChange = (id: string, patch: Partial<ChecklistCriterion>) =>
		setState((s) => ({ ...s, criteria: s.criteria.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit(buildDraft(kind, state));
	};

	const handleDelete = () => {
		if (window.confirm(t('DELETE_CONFIRM'))) onDelete?.();
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-lg">
			<JudgeFormFieldset legend={t('FIELD_NAME')}>
				<input
					required
					type="text"
					value={state.name}
					onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
					className={INPUT_BASE}
				/>
			</JudgeFormFieldset>

			<JudgeFormFieldset legend={t('FIELD_DESCRIPTION')}>
				<input
					type="text"
					value={state.description}
					onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
					className={INPUT_BASE}
				/>
			</JudgeFormFieldset>

			{kind === 'rubric' ? (
				<>
					<JudgeFormFieldset legend={t('FIELD_PROMPT')} hint={t('FIELD_PROMPT_HINT')}>
						<textarea
							required
							value={state.prompt}
							onChange={(e) => setState((s) => ({ ...s, prompt: e.target.value }))}
							className={TEXTAREA_BASE}
						/>
					</JudgeFormFieldset>

					<JudgeFormFieldset legend={t('FIELD_SCALE')}>
						<div className="flex gap-md">
							<label htmlFor="scale-pass-fail" className="flex items-center gap-3xs text-[13px] text-default">
								<input
									id="scale-pass-fail"
									type="radio"
									name="scale"
									value="pass_fail"
									checked={state.scale === 'pass_fail'}
									onChange={() => setState((s) => ({ ...s, scale: 'pass_fail' }))}
									className="accent-purple-500"
								/>
								{t('SCALE_PASS_FAIL')}
							</label>
							<label htmlFor="scale-likert-5" className="flex items-center gap-3xs text-[13px] text-default">
								<input
									id="scale-likert-5"
									type="radio"
									name="scale"
									value="likert_5"
									checked={state.scale === 'likert_5'}
									onChange={() => setState((s) => ({ ...s, scale: 'likert_5' }))}
									className="accent-purple-500"
								/>
								{t('SCALE_LIKERT_5')}
							</label>
						</div>
					</JudgeFormFieldset>

					<JudgeFormFieldset legend={t('FIELD_CONTEXT_TITLE')} hint={t('FIELD_CONTEXT_HINT')}>
						<div className="flex flex-col gap-xs rounded-md border border-subtle bg-grey-25 px-md py-sm">
							<label htmlFor="context-enabled" className="flex items-center gap-xs text-[13px] text-default">
								<input
									id="context-enabled"
									type="checkbox"
									checked={state.contextEnabled}
									onChange={(e) => setState((s) => ({ ...s, contextEnabled: e.target.checked }))}
									className="accent-purple-500"
								/>
								{t('FIELD_CONTEXT_ENABLED')}
							</label>

							{state.contextEnabled && (
								<div className="grid grid-cols-1 gap-md md:grid-cols-[1fr_160px]">
									<label htmlFor="context-namespace" className="flex flex-col gap-2xs">
										<span className="text-[11px] font-medium uppercase tracking-wider text-subtle">
											{t('FIELD_CONTEXT_NAMESPACE')}
										</span>
										<input
											id="context-namespace"
											type="text"
											value={state.contextNamespace}
											placeholder="support"
											onChange={(e) => setState((s) => ({ ...s, contextNamespace: e.target.value }))}
											className={INPUT_BASE}
										/>
									</label>
									<label htmlFor="context-top-k" className="flex flex-col gap-2xs">
										<span className="text-[11px] font-medium uppercase tracking-wider text-subtle">
											{t('FIELD_CONTEXT_TOP_K')}
										</span>
										<input
											id="context-top-k"
											type="number"
											min={1}
											max={20}
											value={state.contextTopK}
											onChange={(e) => setState((s) => ({ ...s, contextTopK: e.target.value }))}
											className={INPUT_BASE}
										/>
									</label>
								</div>
							)}
						</div>
					</JudgeFormFieldset>
				</>
			) : (
				<JudgeFormFieldset legend={t('CRITERIA_TITLE')} hint={t('CRITERIA_SUB')}>
					<ul className="flex flex-col gap-xs">
						{state.criteria.map((c) => (
							<li key={c.id} className="flex flex-col gap-2xs rounded-md border border-subtle bg-grey-25 px-md py-sm">
								<div className="flex items-start gap-xs">
									<input
										type="text"
										placeholder={t('CRITERIA_LABEL')}
										value={c.label}
										onChange={(e) => handleCriterionChange(c.id, { label: e.target.value })}
										className={`${INPUT_BASE} flex-1`}
									/>
									<button
										type="button"
										onClick={() => handleRemoveCriterion(c.id)}
										aria-label={t('CRITERIA_REMOVE')}
										className="inline-flex items-center justify-center rounded-md border border-subtle bg-white px-xs py-3xs text-subtle transition-colors hover:bg-red-50 hover:text-error"
									>
										<Trash2 className="h-sm w-sm" />
									</button>
								</div>
								<input
									type="text"
									placeholder={t('CRITERIA_DESCRIPTION')}
									value={c.description}
									onChange={(e) => handleCriterionChange(c.id, { description: e.target.value })}
									className={INPUT_BASE}
								/>
							</li>
						))}
					</ul>
					<button
						type="button"
						onClick={handleAddCriterion}
						className="mt-xs inline-flex items-center gap-3xs self-start text-[12px] text-purple-700 transition-colors hover:text-purple-500"
					>
						<Plus className="h-sm w-sm" />
						{t('CRITERIA_ADD')}
					</button>
				</JudgeFormFieldset>
			)}

			<div className="grid grid-cols-1 gap-md md:grid-cols-2">
				<JudgeFormFieldset legend={t('FIELD_APPLIES_MODULES')} hint={t('APPLIES_HINT')}>
					<input
						type="text"
						value={state.modulesCsv}
						placeholder="support, sales"
						onChange={(e) => setState((s) => ({ ...s, modulesCsv: e.target.value }))}
						className={INPUT_BASE}
					/>
				</JudgeFormFieldset>
				<JudgeFormFieldset legend={t('FIELD_APPLIES_AGENTS')} hint={t('APPLIES_HINT')}>
					<input
						type="text"
						value={state.agentsCsv}
						placeholder="claudia"
						onChange={(e) => setState((s) => ({ ...s, agentsCsv: e.target.value }))}
						className={INPUT_BASE}
					/>
				</JudgeFormFieldset>
			</div>

			<label htmlFor="judge-enabled" className="flex items-center gap-xs text-[13px] text-default">
				<input
					id="judge-enabled"
					type="checkbox"
					checked={state.enabled}
					onChange={(e) => setState((s) => ({ ...s, enabled: e.target.checked }))}
					className="accent-purple-500"
				/>
				{t('FIELD_ENABLED')}
			</label>

			<div className="flex items-center justify-between border-t border-subtle pt-md">
				<div className="flex items-center gap-xs">
					<button type="submit" disabled={isSubmitting} className={BUTTON_PRIMARY}>
						{isSubmitting ? t('SAVING') : t('SAVE')}
					</button>
					<button type="button" onClick={onCancel} className={BUTTON_GHOST}>
						{t('CANCEL')}
					</button>
				</div>
				{mode === 'edit' && onDelete && (
					<button type="button" onClick={handleDelete} disabled={isDeleting} className={BUTTON_DANGER}>
						{isDeleting ? t('DELETING') : t('DELETE')}
					</button>
				)}
			</div>
		</form>
	);
};
